<template>
  <div class="home-page">
    <!-- Hero + Games 融合区域 -->
    <section class="hero-immersive">
      <div class="hero-decoration">
        <div class="decoration-shape shape-1"></div>
        <div class="decoration-shape shape-2"></div>
        <div class="decoration-shape shape-3"></div>
      </div>
      
      <div class="container hero-container">
        <!-- 头部文字 -->
        <div class="hero-header">
          <h1 class="hero-title animate-fade-in-down">{{ t('hero.title') }}</h1>
          <p class="hero-subtitle animate-fade-in-up">{{ t('hero.subtitle') }}</p>
        </div>

        <!-- 游戏卡片网格 (直接展示) -->
        <div id="games" class="games-grid-immersive">
          <GameCard
            game="poker"
            :title="t('games.poker.title')"
            :description="t('games.poker.description')"
            :difficulty="t('games.poker.difficulty')"
            link="/games/poker"
            class="animate-fade-in-up"
            style="animation-delay: 100ms"
          />
          <GameCard
            game="sudoku"
            :title="t('games.sudoku.title')"
            :description="t('games.sudoku.description')"
            :difficulty="t('games.sudoku.difficulty')"
            link="/games/sudoku"
            class="animate-fade-in-up"
            style="animation-delay: 200ms"
          />
          <GameCard
            game="riddles"
            :title="t('games.riddles.title')"
            :description="t('games.riddles.description')"
            :difficulty="t('games.riddles.difficulty')"
            link="/games/riddles"
            class="animate-fade-in-up"
            style="animation-delay: 300ms"
          />
        </div>
      </div>
    </section>

    <!-- 特性展示区 -->
    <section class="features-section">
      <div class="container">
        <div class="features-grid">
          <div class="feature-card animate-fade-in-up" style="animation-delay: 0ms">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
              </svg>
            </div>
            <h3>{{ t('features.feature1.title') }}</h3>
            <p>{{ t('features.feature1.description') }}</p>
          </div>

          <div class="feature-card animate-fade-in-up" style="animation-delay: 100ms">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h3>{{ t('features.feature2.title') }}</h3>
            <p>{{ t('features.feature2.description') }}</p>
          </div>

          <div class="feature-card animate-fade-in-up" style="animation-delay: 200ms">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18 10 11.41l4 4 6.3-6.29L21 12v-6z"/>
              </svg>
            </div>
            <h3>{{ t('features.feature3.title') }}</h3>
            <p>{{ t('features.feature3.description') }}</p>
          </div>

          <div class="feature-card animate-fade-in-up" style="animation-delay: 300ms">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h3>{{ t('features.feature4.title') }}</h3>
            <p>{{ t('features.feature4.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ 区域 -->
    <section class="faq-section">
      <div class="container">
        <h2 class="section-title">{{ t('faq.title') }}</h2>
        <div class="faq-container">
          <div
            v-for="(item, index) in faqItems"
            :key="index"
            class="faq-item"
            :class="{ active: activeFAQ === index }"
          >
            <button class="faq-question" @click="toggleFAQ(index)">
              <span>{{ item.q }}</span>
              <svg class="faq-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div v-if="activeFAQ === index" class="faq-answer animate-fade-in">
              {{ item.a }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA 最后呼吁 -->
    <section class="final-cta">
      <div class="container">
        <div class="cta-content">
          <h2>{{ t('hero.title') }}</h2>
          <p>{{ t('hero.subtitle') }}</p>
          <button class="cta-button" @click="scrollToGames">{{ t('hero.cta') }}</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from '@/utils/i18n'
import GameCard from '@/components/GameCard.vue'

const { t, currentLang } = useI18n()
const activeFAQ = ref(null)

const faqItems = computed(() => {
  return t('faq.items') || []
})

const toggleFAQ = (index) => {
  activeFAQ.value = activeFAQ.value === index ? null : index
}

const scrollToGames = () => {
  const gamesSection = document.getElementById('games')
  if (gamesSection) {
    gamesSection.scrollIntoView({ behavior: 'smooth' })
  }
}
</script>

<style scoped>
.home-page {
  overflow-x: hidden;
}

/* ========== Immersive Hero + Games 区域 ========== */

.hero-immersive {
  position: relative;
  /* 不再强制 100vh，改为内容自适应 + 充足留白 */
  padding: var(--space-6xl) var(--space-base) var(--space-4xl);
  background: var(--gradient-bg);
  overflow: hidden;
  text-align: center;
}

.hero-container {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

.hero-header {
  max-width: 800px;
  margin: 0 auto var(--space-4xl);
}

.hero-title {
  font-size: clamp(32px, 8vw, 64px);
  font-weight: var(--font-weight-bold);
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 var(--space-base) 0;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: clamp(18px, 4vw, 24px);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

/* 游戏卡片网格 (沉浸式) */
.games-grid-immersive {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--space-2xl);
  perspective: 1000px; /* 增加一点3D透视感 */
}

/* Hero 装饰形状 (保持原有动画，但调整位置适配新布局) */
.hero-decoration {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.decoration-shape {
  position: absolute;
  opacity: 0.1;
  border-radius: var(--radius-full);
}

.shape-1 {
  width: 400px;
  height: 400px;
  background: var(--color-primary-orange);
  top: -100px;
  right: -100px;
  animation: float 6s ease-in-out infinite;
}

.shape-2 {
  width: 300px;
  height: 300px;
  background: var(--color-primary-gold);
  bottom: 10%; /* 调整位置 */
  left: -50px;
  animation: float 8s ease-in-out infinite reverse;
}

.shape-3 {
  width: 200px;
  height: 200px;
  background: var(--color-primary-coral);
  top: 20%;
  left: 10%;
  animation: float 7s ease-in-out infinite;
}

/* ========== 特性展示区 (调整背景区分) ========== */

.features-section {
  padding: var(--space-5xl) var(--space-base);
  background: var(--color-bg-light); /* 与上方 Hero 区分 */
}

.section-title {
  text-align: center;
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-bold);
  margin: 0 0 var(--space-3xl) 0;
  color: var(--color-text-primary);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-2xl);
}

.feature-card {
  background: var(--color-bg-card); /* 适配深色模式 */
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  text-align: center;
  transition: all var(--transition-base) var(--ease-in-out);
  box-shadow: var(--shadow-sm);
}

.feature-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-4px);
  border-color: var(--color-primary-gold);
}

.feature-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto var(--space-base);
  color: var(--color-primary-orange);
}

.feature-icon svg {
  width: 100%;
  height: 100%;
}

.feature-card h3 {
  font-size: var(--font-size-h5);
  margin: 0 0 var(--space-sm) 0;
  color: var(--color-text-primary);
}

.feature-card p {
  color: var(--color-text-secondary);
  margin: 0;
  font-size: var(--font-size-small);
  line-height: 1.6;
}

@media (max-width: 1024px) {
  .hero-immersive {
    padding-top: var(--space-4xl);
  }
  
  .games-grid-immersive {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-xl);
  }
}

@media (max-width: 640px) {
  .hero-immersive {
    padding-top: var(--space-3xl);
    padding-bottom: var(--space-3xl);
  }

  .hero-title {
    font-size: 36px; /* 移动端字号调整 */
  }

  .games-grid-immersive {
    grid-template-columns: 1fr;
    gap: var(--space-lg);
  }

  .features-section {
    padding: var(--space-3xl) var(--space-base);
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: var(--space-lg);
  }

  .feature-card {
    padding: var(--space-xl);
  }
}

/* ========== FAQ 区域 ========== */

.faq-section {
  padding: var(--space-5xl) var(--space-base);
  background: var(--gradient-bg); /* 交替背景 */
}

.faq-container {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.faq-item {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  transition: all var(--transition-base) var(--ease-in-out);
}

.faq-item:hover {
  border-color: var(--color-primary-orange);
  box-shadow: var(--shadow-md);
}

.faq-item.active {
  border-color: var(--color-primary-orange);
  box-shadow: var(--shadow-md);
}

.faq-question {
  width: 100%;
  background: var(--color-bg-card);
  border: none;
  padding: var(--space-lg);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-base);
  transition: all var(--transition-base) var(--ease-in-out);
  font-family: var(--font-heading);
}

.faq-question:hover {
  background: rgba(255, 107, 53, 0.05);
  color: var(--color-primary-orange);
}

.faq-item.active .faq-question {
  background: rgba(255, 107, 53, 0.1);
  color: var(--color-primary-orange);
}

.faq-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: transform var(--transition-base) var(--ease-in-out);
}

.faq-item.active .faq-icon {
  transform: rotate(180deg);
}

.faq-answer {
  padding: var(--space-lg);
  background: var(--color-bg-card);
  border-top: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  line-height: 1.7;
  font-size: var(--font-size-small);
}

@media (max-width: 640px) {
  .faq-section {
    padding: var(--space-3xl) var(--space-base);
  }

  .faq-question {
    padding: var(--space-base);
    font-size: var(--font-size-small);
  }

  .faq-answer {
    padding: var(--space-base);
  }
}

/* ========== 最后 CTA 区域 ========== */

.final-cta {
  padding: var(--space-5xl) var(--space-base);
  background: var(--gradient-primary);
  color: white;
  text-align: center;
}

.cta-content h2 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  color: white;
}

.cta-content p {
  font-size: var(--font-size-body);
  margin: 0 0 var(--space-2xl) 0;
  opacity: 0.95;
}

.cta-button {
  display: inline-flex;
  background: var(--color-bg-light);
  color: var(--color-primary-orange);
  padding: var(--space-md) var(--space-2xl);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  border: none;
  transition: all var(--transition-base) var(--ease-in-out);
  cursor: pointer;
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.cta-button:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

@media (max-width: 640px) {
  .final-cta {
    padding: var(--space-3xl) var(--space-base);
  }

  .cta-content h2 {
    font-size: var(--font-size-h2);
  }
}
</style>
